import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// 格式化答案：识别编号、加粗、分段、分隔线
function formatAnswer(text) {
  if (!text) return null;
  const lines = (text || '').split('\n');
  const elements = [];
  let key = 0;
  
  for (const line of lines) {
    const trimmed = (line || '').trim();
    if (!trimmed) {
      elements.push(<div key={key++} style={{ height: 8 }} />);
      continue;
    }
    
    // 分隔线
    if (trimmed === '---') {
      elements.push(<hr key={key++} style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #e8e8e8' }} />);
      continue;
    }
    
    // 引用块（> 开头）
    if (trimmed.startsWith('>')) {
      const content = trimmed.replace(/^>\s*/, '');
      elements.push(
        <div key={key++} className="qa-answer-reference">
          {formatInline(content)}
        </div>
      );
      continue;
    }
    
    // 加粗标题（**xxx**）
    const boldMatch = trimmed.match(/^\*\*([^*]+)\*\*[：:]?\s*(.*)$/);
    if (boldMatch) {
      const title = boldMatch[1];
      const rest = boldMatch[2];
      elements.push(
        <div key={key++} style={{ marginBottom: 8 }}>
          <strong style={{ color: '#1890ff', fontSize: 15 }}>{title}</strong>
          {rest && <span style={{ marginLeft: 8 }}>{formatInline(rest)}</span>}
        </div>
      );
      continue;
    }
    
    // 列表项（- 开头）
    if (trimmed.startsWith('- ')) {
      const content = trimmed.substring(2);
      elements.push(
        <div key={key++} style={{ 
          paddingLeft: 20, 
          marginBottom: 6, 
          position: 'relative',
          color: '#595959'
        }}>
          <span style={{ 
            position: 'absolute', 
            left: 8, 
            color: '#bfbfbf' 
          }}>•</span>
          {formatInline(content)}
        </div>
      );
      continue;
    }
    
    const numMatch = trimmed.match(/^(\d+)[.\、\、）\)]\s*/);
    
    if (numMatch && numMatch[1] && !trimmed.startsWith('根据')) {
      const rest = trimmed.replace(/^(\d+)[.\、\、）\)]\s*/, '');
      elements.push(
        <div key={key++} className="qa-answer-step">
          <span className="qa-step-num">{numMatch[1]}</span>
          <span className="qa-step-text">{formatInline(rest)}</span>
        </div>
      );
    } else if (trimmed.startsWith('注意') || trimmed.startsWith('提示')) {
      elements.push(
        <div key={key++} className="qa-answer-note">
          ⚠️ {formatInline(trimmed.replace(/^(注意[：:]?|提示[：:]?)\s*/, ''))}
        </div>
      );
    } else if (trimmed.startsWith('根据')) {
      elements.push(
        <div key={key++} className="qa-answer-reference">
          {formatInline(trimmed)}
        </div>
      );
    } else {
      elements.push(
        <div key={key++} className="qa-answer-line">{formatInline(trimmed)}</div>
      );
    }
  }
  
  return elements;
}

function formatInline(text) {
  const safeText = text || '';
  const parts = safeText.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

export default function FinanceQA({ embedded }) {
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [sources, setSources] = useState([]);
  const [sourceType, setSourceType] = useState('local');
  const [sourceLabel, setSourceLabel] = useState('');
  const [disclaimer, setDisclaimer] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem('finance_qa_history');
    if (saved) {
      try { setHistory(JSON.parse(saved)); } catch {}
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim() || loading) return;

    setLoading(true);
    setAnswer('');
    setSources([]);
    setSourceType('local');
    setSourceLabel('');
    setDisclaimer('');

    try {
      const resp = await fetch(`${API_URL}/api/knowledge/qa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: question.trim(), type: 'finance' })
      });
      const data = await resp.json();

      if (data.success) {
        setAnswer(data.answer);
        setSources(data.sources || []);
        setSourceType(data.sourceType || 'local');
        setSourceLabel(data.sourceLabel || '');
        
        const newHistory = [{ q: question.trim(), a: data.answer, time: new Date().toISOString() }, ...history.slice(0, 19)];
        setHistory(newHistory);
        localStorage.setItem('finance_qa_history', JSON.stringify(newHistory));

        fetch(`${API_URL}/api/knowledge/qa/log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            question: question.trim(),
            answer: data.answer,
            sourceType: data.sourceType,
            sources: data.sources
          })
        }).catch(() => {});
      } else {
        setAnswer(data.error || '查询失败');
      }
    } catch (err) {
      setAnswer('请求失败: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={embedded ? '' : 'dashboard-container'}>
      <div className="dashboard-header">
        <h2>💰 财务知识问答</h2>
        <p>基于CPA教材、中级会计教材和题库的专业财务问答</p>
      </div>

      <form onSubmit={handleSubmit} className="qa-form">
        <div className="qa-input-group">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="请输入财务相关问题，如：长期股权投资的初始计量..."
            className="qa-input"
          />
          <button type="submit" disabled={loading || !question.trim()} className="qa-submit">
            {loading ? '搜索中...' : '提问'}
          </button>
        </div>
      </form>

      {loading && (
        <div className="qa-loading">
          <div className="knowledge-spinner" />
          <p>正在搜索财务知识库并分析...</p>
        </div>
      )}

      {answer && !loading && (
        <div className="qa-answer-box">
          <div className="qa-answer-header">
            <span className="qa-answer-icon">💡</span>
            <span>AI回答</span>
            <span className={`qa-answer-badge ${sourceType === 'ai' ? 'qa-badge-ai' : ''}`}>
              {sourceLabel || (sourceType === 'ai' ? '🤖 AI知识' : '📚 财务知识库')}
            </span>
          </div>
          
          <div className="qa-answer-content">
            {formatAnswer(answer)}
          </div>
          
      {disclaimer && (
            <div className="qa-disclaimer" style={{ marginBottom: 16 }}>
              ⚠️ {disclaimer}
            </div>
          )}
          
          {sources.length > 0 && (
            <div className="qa-sources">
              <div className="qa-sources-header">
                <span>📚 知识来源</span>
                <span className="qa-sources-count">{Math.min(sources.length, 5)}条相关</span>
              </div>
              <div className="qa-sources-list-detail">
                {sources.slice(0, 5).map((s, idx) => (
                  <div key={idx} className="qa-source-item">
                    <div className="qa-source-num">#{idx + 1}</div>
                    <div className="qa-source-content">
                      <div className="qa-source-q">{s.q}</div>
                      <div className="qa-source-a">
                        {s.a ? (s.a.length > 200 ? s.a.substring(0, 200) + '...' : s.a) : '无答案'}
                      </div>
                      <div className="qa-source-meta">
                        <span className="qa-source-from">📂 {s.source}</span>
                        {s.product && <span className="qa-tag">{s.product}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && history.length > 0 && (
        <div className="qa-history">
          <div className="qa-history-header">
            <span>🕐 历史提问</span>
            <button onClick={() => { setHistory([]); localStorage.removeItem('finance_qa_history'); }} className="qa-history-clear">
              清空
            </button>
          </div>
          <div className="qa-history-list">
            {history.slice(0, 10).map((item, idx) => (
              <div key={idx} className="qa-history-item" onClick={() => setQuestion(item.q)}>
                <div className="qa-history-q">{item.q}</div>
                <div className="qa-history-time">{new Date(item.time).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
